import React, { useMemo } from "react";
import ExcelGrid from "components/ExcelGrid";

const TEXT_CLASS = "text-app text-[12px]";
const NUMBER_CLASS = "font-mono text-app text-[13px] text-right";

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const formatted = Math.abs(number).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return number < 0 ? `(${formatted})` : formatted;
}

function formatYield(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${(number * 100).toFixed(3)}%` : "—";
}

const FUND_COLUMN = {
  key: "fundName",
  label: "Fund",
  width: "min-w-[240px]",
  className: TEXT_CLASS,
};

function currencyColumn(key, label, width = "min-w-[180px]") {
  return {
    key,
    label,
    width,
    className: NUMBER_CLASS,
    headerClassName: "text-right",
    numeric: true,
    render: (row) => formatCurrency(row[key]),
    copyValue: (row) => formatCurrency(row[key]),
  };
}

const PERIOD_COLUMNS = [
  FUND_COLUMN,
  currencyColumn("beginningMarketValue", "Beginning Market Value", "min-w-[190px]"),
  currencyColumn("endingMarketValue", "Ending Market Value", "min-w-[190px]"),
  currencyColumn("changeInMarketValue", "Change in MV", "min-w-[170px]"),
  currencyColumn("beginningBookValue", "Beginning Book Value"),
  currencyColumn("endingBookValue", "Ending Book Value"),
  currencyColumn("changeInBookValue", "Change in BV"),
  currencyColumn("fullyAccruedInterest", "Accrued Interest", "min-w-[170px]"),
  {
    key: "yield",
    label: "Yield",
    width: "min-w-[110px]",
    className: NUMBER_CLASS,
    headerClassName: "text-right",
    numeric: true,
    render: (row) => formatYield(row.yield),
    copyValue: (row) => formatYield(row.yield),
  },
];

const DATE_COLUMNS = [
  FUND_COLUMN,
  currencyColumn("endingBookValue", "Book Value"),
  currencyColumn("endingMarketValue", "Market Value"),
  currencyColumn("fullyAccruedInterest", "Accrued Interest"),
];

export default function FundOverviewReport({ data }) {
  const columns = data?.reportMode === "period" ? PERIOD_COLUMNS : DATE_COLUMNS;
  const rows = useMemo(() => {
    const fundRows = [...(data?.rows || [])].sort(
      (a, b) => a.fundName.localeCompare(b.fundName)
    );
    return data?.totals ? [...fundRows, data.totals] : fundRows;
  }, [data?.rows, data?.totals]);

  return (
    <ExcelGrid
      columns={columns}
      rows={rows}
      onChange={() => {}}
      rowClassName={(row) => row.fundId === "__total__" ? "bg-muted/50 font-semibold" : ""}
      copyTableHeading="Fund Overview"
      copyTableAsHtml
    />
  );
}
